// src/ui/screens/chats/widgets/MessageSelectionBar.jsx

import React from 'react';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
};

// ── Bar Button ──────────────────────────────────────────────────────────────

const BarButton = ({ icon, onTap, color = null, tooltip = null }) => {
  const getIconPath = () => {
    switch (icon) {
      case 'close':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        );
      case 'reply':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
        );
      case 'pin':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9z" />
        );
      case 'forward':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
        );
      case 'delete':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        );
      default:
        return null;
    }
  };

  const isDisabled = !onTap;
  const iconColor = isDisabled
    ? 'rgba(255,255,255,0.24)'
    : (color || 'rgba(255,255,255,0.85)');

  const button = (
    <button
      onClick={onTap || undefined}
      className="w-11 h-11 flex items-center justify-center rounded-full transition-colors hover:bg-white/5 disabled:opacity-40"
      disabled={isDisabled}
      title={tooltip || ''}
    >
      <svg className="w-6 h-6" fill="none" stroke={iconColor} viewBox="0 0 24 24">
        {getIconPath()}
      </svg>
    </button>
  );

  return button;
};

// ── MessageSelectionBar ─────────────────────────────────────────────────────

export const MessageSelectionBar = ({
  selectedCount,
  canReply = false,
  canPin = false,
  canDeleteForEveryone = false,
  onClose,
  onReply = null,
  onPin = null,
  onDelete,
  onForward,
}) => {
  // ── Calculate status bar height ──────────────────────────────────────────

  const getStatusBarHeight = () => {
    if (typeof window !== 'undefined') {
      // Try to get from CSS env or use a reasonable default
      return 44; // iOS default, Android varies
    }
    return 0;
  };

  const statusBarHeight = getStatusBarHeight();

  return (
    <div
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-200"
      style={{
        paddingTop: statusBarHeight,
        height: statusBarHeight + 56 + 12,
        backgroundColor: 'rgba(8,9,11,0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: `0.5px solid ${COLORS.orange}2E`,
      }}
    >
      <div className="flex items-center h-full px-1">
        {/* Close / Deselect */}
        <BarButton icon="close" onTap={onClose} />

        <div className="w-1" />

        {/* Count Badge */}
        <div
          className="px-2.5 py-1 rounded-full border transition-all duration-200"
          style={{
            backgroundColor: `${COLORS.orange}26`,
            borderColor: `${COLORS.orange}59`,
          }}
        >
          <span className="text-filatom-primary text-sm font-bold">
            {selectedCount}
          </span>
        </div>

        <div className="flex-1" />

        {/* Reply - single selection only */}
        {canReply && (
          <BarButton icon="reply" onTap={onReply} tooltip="Reply" />
        )}

        {/* Pin - single selection only */}
        {canPin && (
          <BarButton icon="pin" onTap={onPin} tooltip="Pin" />
        )}

        {/* Forward */}
        <BarButton icon="forward" onTap={onForward} tooltip="Forward" />

        {/* Delete */}
        <BarButton
          icon="delete"
          onTap={onDelete}
          color="#FF1744"
          tooltip="Delete"
        />
      </div>
    </div>
  );
};

export default MessageSelectionBar;